import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Table,
} from "reactstrap";

import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import { supportRequestsRepo } from "data/repositories/supportRequestsRepo";
import { toDateLabel } from "admin/contentUtils";

const STATUS_COLOR = {
  open: "warning",
  in_progress: "info",
  resolved: "success",
  closed: "secondary",
};

const STATUS_LABEL = {
  open: "Açık",
  in_progress: "İşlemde",
  resolved: "Çözüldü",
  closed: "Kapatıldı",
};

const CATEGORY_LABEL = {
  account: "Hesap",
  technical: "Teknik",
  booking: "Rezervasyon",
  privacy: "Gizlilik",
  feedback: "Geri bildirim",
  other: "Diğer",
};

const sortOptions = [
  { value: "created-desc", label: "En yeni talep" },
  { value: "status-asc", label: "Duruma göre" },
  { value: "name-asc", label: "İsme göre" },
];

export default function AdminSupportRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortBy, setSortBy] = useState("created-desc");

  const load = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      setRequests(supportRequestsRepo.list());
    } catch (loadError) {
      setError(loadError?.message || "Destek talepleri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = requests.filter((request) => {
      if (statusFilter && request.status !== statusFilter) return false;
      if (categoryFilter && request.category !== categoryFilter) return false;
      if (!query) return true;

      return [
        request.id,
        request.name,
        request.email,
        request.phone,
        request.subject,
        request.message,
        request.category,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });

    return [...next].sort((left, right) => {
      if (sortBy === "status-asc") {
        return String(left.status || "").localeCompare(String(right.status || ""), "tr");
      }
      if (sortBy === "name-asc") {
        return String(left.name || "").localeCompare(String(right.name || ""), "tr");
      }
      return String(right.createdAt || "").localeCompare(String(left.createdAt || ""));
    });
  }, [categoryFilter, requests, search, sortBy, statusFilter]);

  const handleStatusChange = (id, status) => {
    try {
      supportRequestsRepo.updateStatus(id, status);
      load();
    } catch (updateError) {
      setError(updateError?.message || "Durum güncellenemedi");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setCategoryFilter("");
    setSortBy("created-desc");
  };

  const openCount = requests.filter((request) => request.status === "open").length;

  return (
    <>
      <AdminPageHeader
        title="Yönetim — Destek Talepleri"
        description={`Kullanıcıların /destek sayfasından gönderdiği talepler. Açık talep: ${openCount}`}
      />
      <Container className="mt--6" fluid>
        <Card className="shadow">
          <CardHeader>
            <h3 className="mb-3">Gelen destek talepleri</h3>
            <AdminListControls
              search={search}
              onSearch={setSearch}
              searchPlaceholder="İsim, e-posta veya mesaj ara"
              filters={[
                {
                  key: "status",
                  label: "Durum",
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: "", label: "Tüm durumlar" },
                    ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
                  ],
                },
                {
                  key: "category",
                  label: "Konu",
                  value: categoryFilter,
                  onChange: setCategoryFilter,
                  options: [
                    { value: "", label: "Tüm konular" },
                    ...Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label })),
                  ],
                },
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              sortOptions={sortOptions}
              onReset={resetFilters}
              resultCount={filtered.length}
              totalCount={requests.length}
            />
          </CardHeader>
          <CardBody>
            <AdminStateBlock
              error={error}
              onDismissError={() => setError("")}
              loading={loading}
              spinnerColor="dark"
              isEmpty={!filtered.length}
              emptyTitle="Destek talebi yok"
            >
              <Table responsive className="align-items-center">
                <thead className="thead-light">
                  <tr>
                    <th>Gönderen</th>
                    <th>Konu</th>
                    <th>Mesaj</th>
                    <th>Durum</th>
                    <th>Tarih</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <div className="font-weight-bold">{request.name}</div>
                        <small className="text-muted d-block">{request.email}</small>
                        {request.phone ? <small className="text-muted d-block">{request.phone}</small> : null}
                      </td>
                      <td>
                        <Badge color="primary" pill className="mb-1">
                          {CATEGORY_LABEL[request.category] || request.category}
                        </Badge>
                        <div>{request.subject || "-"}</div>
                      </td>
                      <td style={{ maxWidth: 280 }}>
                        <small className="text-muted">{request.message}</small>
                      </td>
                      <td>
                        <Badge color={STATUS_COLOR[request.status] || "secondary"} pill>
                          {STATUS_LABEL[request.status] || request.status}
                        </Badge>
                      </td>
                      <td>{toDateLabel(request.createdAt)}</td>
                      <td className="text-right">
                        {request.status === "open" ? (
                          <Button size="sm" color="info" onClick={() => handleStatusChange(request.id, "in_progress")}>
                            İşleme al
                          </Button>
                        ) : null}
                        {request.status === "in_progress" ? (
                          <Button size="sm" color="success" onClick={() => handleStatusChange(request.id, "resolved")}>
                            Çözüldü
                          </Button>
                        ) : null}
                        {request.status !== "closed" ? (
                          <Button
                            size="sm"
                            color="secondary"
                            className="ml-2"
                            onClick={() => handleStatusChange(request.id, "closed")}
                          >
                            Kapat
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </AdminStateBlock>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
